import React, { useState } from "react";
import { Mail, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { SOCIALS } from "@/lib/socials";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          <h1 className="text-4xl font-[680] tracking-[-0.02em] text-foreground">Get in touch</h1>
          <p className="mt-4 text-muted-foreground">Questions, feedback, or partnership ideas? We&apos;d love to hear from you.</p>
          <div className="mt-8 space-y-4">
            <a href="mailto:hello@startupotter.com" className="flex items-center gap-3 text-foreground hover:text-primary">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50"><Mail className="h-5 w-5 text-primary" /></span>
              hello@startupotter.com
            </a>
            <div className="flex flex-wrap items-center gap-3 pt-2" data-testid="contact-socials">
              {SOCIALS.map(({ href, label, testId, Icon, disabled }) => (
                <a
                  key={testId}
                  href={href}
                  target={disabled ? undefined : "_blank"}
                  rel={disabled ? undefined : "noreferrer"}
                  aria-label={label}
                  title={label}
                  data-testid={`contact-${testId}`}
                  onClick={disabled ? (e) => e.preventDefault() : undefined}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-primary hover:bg-teal-50 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-submit-button">
              {submitting ? "Sending…" : <>Send message <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
